import { Injectable, inject, isDevMode } from '@angular/core';

import { BaseConfig, SHARED_CONFIG, SharedConfig } from '@shared/models';

@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  private readonly sharedConfig = inject<SharedConfig>(SHARED_CONFIG);

  public model: BaseConfig | undefined;

  public get config(): BaseConfig | undefined {
    return this.model;
  }

  public get isLocal() {
    return window.location.hostname.includes('localhost');
  }

  public get identityServerUrl(): string {
    return this.model?.identityServerUrl || '';
  }

  public get applicationName(): string {
    if (this.model?.clientDomains) {
      const clientDomain = this.model.clientDomains.find(x => x.domain === window.location.host);
      if (clientDomain) {
        return clientDomain.client;
      }
    }

    return this.sharedConfig.applicationName;
  }

  public get userPilotToken(): string | undefined {
    return this.model?.userPilotToken;
  }

  public get allowDarkMode(): boolean {
    return this.model?.allowDarkMode === true;
  }

  public async load(): Promise<unknown> {
    const configResponse = await fetch('assets/config.json');
    this.model = await configResponse.json();

    if (isDevMode()) {
      const localResponse = await fetch('assets/config.local.json', {
        headers: { accept: 'application/json' },
        cache: 'no-store',
      });

      if (localResponse.status !== 404) {
        if (!localResponse.ok) {
          throw new Error(`Failed to load local configuration: HTTP ${localResponse.status}`);
        }

        const overrides: unknown = await localResponse.json();
        if (!overrides || typeof overrides !== 'object' || Array.isArray(overrides)) {
          throw new Error('Local configuration must be a JSON object');
        }

        this.model = { ...this.model, ...overrides } as BaseConfig;
      }
    }

    return this.model;
  }
}
